import { Badge } from "@/components/ui/badge";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef } from "react";

const badgeVariants = cva(
  "text-xs backdrop-blur-sm border shadow-sm",
  {
    variants: {
      variant: {
        completed: "bg-green-100/60 border-green-200/50 text-green-800",
        signupLimit: "bg-orange-100/60 border-orange-200/50 text-orange-800",
        publicSignups: "bg-emerald-100/60 border-emerald-200/50 text-emerald-800",
        manualRegistration: "bg-gray-100/60 border-gray-200/50 text-gray-800",
        performers: "bg-slate-100/60 border-slate-200/50 text-slate-800",
        date: "bg-white/30 backdrop-blur-sm border-white/20 text-slate-700 shadow-sm",
        jam: "bg-blue-100/60 border-blue-200/50 text-blue-800",
        openmic: "bg-purple-100/60 border-purple-200/50 text-purple-800",
        event: "bg-green-100/60 border-green-200/50 text-green-800",
        godMode: "bg-red-600/80 backdrop-blur-sm border-red-500/30",
        messageNotification: "bg-red-600/80 backdrop-blur-sm border-red-500/30",
      },
    },
    defaultVariants: {
      variant: "performers",
    },
  }
);

const boxShadows = {
  completed: '0 2px 8px rgba(34,197,94,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
  signupLimit: '0 2px 8px rgba(251,146,60,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
  publicSignups: '0 2px 8px rgba(16,185,129,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
  manualRegistration: '0 2px 8px rgba(107,114,128,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
  performers: '0 2px 8px rgba(71,85,105,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
  date: undefined,
  jam: '0 2px 8px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
  openmic: '0 2px 8px rgba(168,85,247,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
  event: '0 2px 8px rgba(34,197,94,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
  godMode: '0 2px 8px rgba(220,38,38,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
  messageNotification: '0 2px 4px rgba(220,38,38,0.3), inset 0 1px 0 rgba(255,255,255,0.3)',
};

export interface StyledBadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

const StyledBadge = forwardRef<HTMLDivElement, StyledBadgeProps>(({ className, variant, ...props }, ref) => {
  const boxShadow = variant ? boxShadows[variant] : undefined;
  return (
    <Badge
        // @ts-ignore
        ref={ref}
      className={badgeVariants({ variant, className })}
      style={{ boxShadow }}
      {...props}
    />
  );
});

StyledBadge.displayName = "StyledBadge";

export { StyledBadge };
